import type { TFunction } from 'i18next';

type TimelineDisplay = {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  badgeLabel?: string;
  badgeClass?: string;
  icon?: string;
  amountPrefix?: string;
  attemptLabel?: string;
};

// "Etapp 4" or "Etappmål #4" only repeats the number.
const isGenericStageName = (description: string) => /^(etapp(mål)?|stage)\s*#?\s*\d+$/i.test(description);

export const milestonePaymentDisplay = (transaction: {
  type?: string;
  metadata?: {
    milestoneNumber?: number | null;
    contractNumber?: number | null;
    milestoneDescription?: string | null;
    companyName?: string | null;
  };
  display: TimelineDisplay;
}, t: TFunction): TimelineDisplay => {
  if (transaction.type !== 'milestone_released') return transaction.display;
  const number = transaction.metadata?.milestoneNumber;
  const contractNumber = transaction.metadata?.contractNumber;
  const companyName = transaction.metadata?.companyName?.trim();
  const info = t('projectView.transactions.milestoneReleasedInfo', {
    defaultValue: 'Utbetalningen för etappen är bokad.',
  });
  const badgeLabel = t('projectView.transactions.milestoneReleasedBadge', { defaultValue: 'Utbetald' });
  // Say which company the money went to, with the contract and stage below.
  if (companyName) {
    const description = transaction.metadata?.milestoneDescription?.trim();
    const stage = number
      ? description && !isGenericStageName(description)
        ? t('projectView.transactions.milestoneReleasedStageNamed', { number, description })
        : t('projectView.transactions.milestoneReleasedStage', { number })
      : null;
    const contract = contractNumber
      ? `${t('projectView.transactions.contractRef', { defaultValue: 'Kontrakt' })} #${contractNumber}`
      : null;
    const subtitle = [contract, stage].filter(Boolean).join(' · ') || info;
    return {
      ...transaction.display,
      title: t('projectView.transactions.milestoneReleasedTo', { company: companyName }),
      subtitle,
      tooltip: subtitle,
      badgeLabel,
    };
  }
  return {
    ...transaction.display,
    title: number
      ? t('projectView.transactions.milestoneReleasedTitle', {
          defaultValue: 'Etappmål #{{number}} utbetalt', number,
        })
      : t('projectView.transactions.status.customerReleased', { defaultValue: 'Utbetalning utförd' }),
    subtitle: contractNumber
      ? `${t('projectView.transactions.contractRef', { defaultValue: 'Kontrakt' })} #${contractNumber} · ${info}`
      : info,
    tooltip: info,
    badgeLabel,
  };
};
